import React, { useState } from 'react';
import { 
  ChartLineUp, 
  Warning, 
  CheckCircle, 
  XCircle,
  CaretDown,
  CaretUp,
  TrendUp,
  TrendDown,
  Lightning,
  FirstAid,
  ShieldWarning,
  Target,
  ChartBar,
  Lock,
  Star
} from '@phosphor-icons/react';

export const MatchAnalysisCard = ({ match, analysis, userPlan = 'free' }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isVip = userPlan === 'vip';
  const isNormal = userPlan === 'normal';
  const isFree = userPlan === 'free';

  const getVerdictConfig = (verdict) => {
    switch (verdict) {
      case 'good_bet':
        return { 
          label: 'Good Bet', 
          color: 'text-risk-low',
          bgColor: 'bg-risk-low/15',
          borderColor: 'border-risk-low/30',
          icon: CheckCircle 
        };
      case 'caution':
        return { 
          label: 'Caution', 
          color: 'text-risk-medium',
          bgColor: 'bg-risk-medium/15',
          borderColor: 'border-risk-medium/30',
          icon: Warning 
        };
      case 'no_bet':
        return { 
          label: 'No Bet', 
          color: 'text-risk-high',
          bgColor: 'bg-risk-high/15',
          borderColor: 'border-risk-high/30',
          icon: XCircle 
        };
      default:
        return { 
          label: 'Analysis', 
          color: 'text-white/50',
          bgColor: 'bg-white/5',
          borderColor: 'border-white/10',
          icon: ChartLineUp 
        };
    }
  };

  const getRiskLevelConfig = (riskScore) => {
    if (riskScore < 35) {
      return { label: 'Low Risk', color: 'text-risk-low', icon: CheckCircle };
    } else if (riskScore < 60) {
      return { label: 'Medium Risk', color: 'text-risk-medium', icon: Warning };
    } else {
      return { label: 'High Risk', color: 'text-risk-high', icon: ShieldWarning };
    }
  };

  const getImpactLabel = (impact) => {
    if (impact < 25) return { label: 'Low', color: 'text-risk-low' };
    if (impact < 50) return { label: 'Medium', color: 'text-risk-medium' };
    return { label: 'High', color: 'text-risk-high' };
  };

  if (!analysis) {
    return null;
  }

  const verdictConfig = getVerdictConfig(analysis.overall_verdict);
  const VerdictIcon = verdictConfig.icon;
  const avgConfidence = Math.round((analysis.home_team.confidence_score + analysis.away_team.confidence_score) / 2);
  const avgRisk = Math.round((analysis.home_team.risk_score + analysis.away_team.risk_score) / 2);
  const riskConfig = getRiskLevelConfig(avgRisk);
  const RiskIcon = riskConfig.icon;

  const allPositiveFactors = [...analysis.home_team.positive_factors, ...analysis.away_team.positive_factors];
  const allNegativeFactors = [...analysis.home_team.negative_factors, ...analysis.away_team.negative_factors];
  const allMissingPlayers = [
    ...analysis.home_team.missing_players.map(p => ({ ...p, team: analysis.home_team.name })),
    ...analysis.away_team.missing_players.map(p => ({ ...p, team: analysis.away_team.name }))
  ];

  return (
    <div className="glass-surface rounded-sm border-white/10 overflow-hidden">
      {/* Compact Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-white/5 transition-colors duration-150 group"
        data-testid={`match-analysis-toggle-${match.match_id}`}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm bg-secondary/10 border border-secondary/30 flex items-center justify-center">
            <ChartLineUp weight="duotone" className="text-secondary text-xl" />
          </div>
          <div className="text-left">
            <div className="text-white font-heading font-bold text-sm">Advanced AI Analysis</div>
            <div className="text-white/40 text-xs">Click to view detailed breakdown</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full border ${verdictConfig.borderColor} ${verdictConfig.bgColor}`}>
            <VerdictIcon weight="fill" className={`${verdictConfig.color} text-base`} />
            <span className={`${verdictConfig.color} font-bold text-sm uppercase tracking-wide`}>{verdictConfig.label}</span>
          </div>
          {isExpanded ? (
            <CaretUp weight="bold" className="text-white/30 text-xl group-hover:text-white/50 transition-colors" />
          ) : (
            <CaretDown weight="bold" className="text-white/30 text-xl group-hover:text-white/50 transition-colors" />
          )}
        </div>
      </button>

      {/* Expanded Analysis */}
      {isExpanded && (
        <div className="px-6 pb-6 space-y-5">
          {isFree && (
            <div className="bg-risk-medium/10 border border-risk-medium/30 rounded-sm p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Lock weight="fill" className="text-risk-medium text-xl" />
                <div>
                  <div className="text-risk-medium font-bold text-sm">Free Plan - Limited Access</div>
                  <div className="text-white/50 text-xs">Upgrade to Normal for full analysis or VIP for advanced insights</div>
                </div>
              </div>
            </div>
          )}

          {isNormal && (
            <div className="bg-secondary/10 border border-secondary/30 rounded-sm p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Star weight="fill" className="text-secondary text-xl" />
                <div>
                  <div className="text-secondary font-bold text-sm">Normal Plan Active</div>
                  <div className="text-white/50 text-xs">Upgrade to VIP for advanced AI insights and value bets detection</div>
                </div>
              </div>
            </div>
          )}

          {/* Summary Section */}
          <div className="grid md:grid-cols-3 gap-4">
            {/* Confidence Score */}
            <div className="bg-white/5 border border-white/10 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <Target weight="duotone" className="text-primary text-lg" />
                <span className="text-xs font-bold text-white/50 uppercase tracking-wide">Confidence</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-mono font-black text-primary">{avgConfidence}</span>
                <span className="text-white/40 text-sm">/ 100</span>
              </div>
              <div className="mt-2 h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all duration-500"
                  style={{ width: `${avgConfidence}%` }}
                />
              </div>
            </div>

            {/* Risk Level */}
            <div className="bg-white/5 border border-white/10 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <ChartBar weight="duotone" className="text-secondary text-lg" />
                <span className="text-xs font-bold text-white/50 uppercase tracking-wide">Risk Level</span>
              </div>
              <div className="flex items-center gap-2">
                <RiskIcon weight="fill" className={`${riskConfig.color} text-2xl`} />
                <span className={`text-xl font-bold ${riskConfig.color}`}>{riskConfig.label}</span>
              </div>
              <div className="mt-2 text-white/40 text-xs font-mono">{avgRisk}% Risk Score</div>
            </div>

            {/* Match Importance */}
            <div className="bg-white/5 border border-white/10 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <Lightning weight="duotone" className="text-secondary text-lg" />
                <span className="text-xs font-bold text-white/50 uppercase tracking-wide">Importance</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-mono font-black text-secondary">{analysis.match_context.importance}</span>
                <span className="text-white/40 text-sm">/ 10</span>
              </div>
              <div className="mt-2 text-white/60 text-sm font-semibold">{analysis.match_context.importance_label}</div>
            </div>
          </div>

          {/* AI Explanation - Prominent */}
          <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-sm p-5">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-sm bg-primary/20 border border-primary/30 flex items-center justify-center flex-shrink-0">
                <Lightning weight="fill" className="text-primary text-base" />
              </div>
              <div className="flex-1">
                <div className="font-heading font-bold text-white mb-2 text-sm uppercase tracking-wide">AI Analysis</div>
                <p className="text-white/80 leading-relaxed text-sm">{analysis.ai_explanation}</p>
              </div>
            </div>
          </div>

          {/* Key Factors Grid */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Positive Factors */}
            {allPositiveFactors.length > 0 && (
              <div className="bg-risk-low/5 border border-risk-low/20 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-sm bg-risk-low/20 flex items-center justify-center">
                    <TrendUp weight="bold" className="text-risk-low text-sm" />
                  </div>
                  <span className="text-xs font-bold text-risk-low uppercase tracking-wide">Positive Factors</span>
                </div>
                <div className="space-y-2">
                  {allPositiveFactors.slice(0, 5).map((factor, idx) => (
                    <div key={idx} className="flex items-start gap-2.5">
                      <CheckCircle weight="fill" className="text-risk-low text-base mt-0.5 flex-shrink-0" />
                      <span className="text-white/70 text-sm leading-snug">{factor}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Negative Factors */}
            {allNegativeFactors.length > 0 && (
              <div className="bg-risk-high/5 border border-risk-high/20 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-sm bg-risk-high/20 flex items-center justify-center">
                    <TrendDown weight="bold" className="text-risk-high text-sm" />
                  </div>
                  <span className="text-xs font-bold text-risk-high uppercase tracking-wide">Negative Factors</span>
                </div>
                <div className="space-y-2">
                  {allNegativeFactors.slice(0, 5).map((factor, idx) => (
                    <div key={idx} className="flex items-start gap-2.5">
                      <XCircle weight="fill" className="text-risk-high text-base mt-0.5 flex-shrink-0" />
                      <span className="text-white/70 text-sm leading-snug">{factor}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Missing Players - Only if present */}
          {allMissingPlayers.length > 0 && (
            <div className="bg-risk-high/10 border border-risk-high/25 rounded-sm p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-sm bg-risk-high/20 border border-risk-high/30 flex items-center justify-center">
                  <FirstAid weight="duotone" className="text-risk-high text-base" />
                </div>
                <div>
                  <div className="font-bold text-risk-high text-sm uppercase tracking-wide">Missing Key Players</div>
                  <div className="text-white/40 text-xs">Injuries & Suspensions</div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {allMissingPlayers.map((player, idx) => {
                  const impact = getImpactLabel(
                    player.position === 'Goalkeeper' ? 30 :
                    player.position === 'Striker' ? 25 :
                    player.position === 'Midfielder' ? 20 : 15
                  );
                  return (
                    <div key={idx} className="bg-white/5 border border-white/10 rounded-sm p-3">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="font-semibold text-white text-sm">{player.name}</div>
                        <span className={`text-xs font-bold ${impact.color} uppercase`}>{impact.label}</span>
                      </div>
                      <div className="text-white/40 text-xs">{player.position} - {player.team}</div>
                      <div className="mt-1 text-white/50 text-xs">
                        <span className={`px-2 py-0.5 rounded-full ${player.reason === 'Injury' ? 'bg-risk-high/20 text-risk-high' : 'bg-risk-medium/20 text-risk-medium'}`}>
                          {player.reason}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Team Stats - Compact */}
          <details className="group">
            <summary className="cursor-pointer list-none flex items-center justify-between py-3 px-4 bg-white/5 hover:bg-white/10 rounded-sm transition-colors">
              <span className="text-white/70 text-sm font-semibold">View Detailed Team Statistics</span>
              <CaretDown weight="bold" className="text-white/30 text-lg group-open:rotate-180 transition-transform" />
            </summary>
            <div className="mt-3 grid md:grid-cols-2 gap-4 px-2">
              {/* Home Team Stats */}
              <div className="bg-white/5 border border-white/10 rounded-sm p-4">
                <div className="font-heading font-bold text-white mb-3">{analysis.home_team.name}</div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/50">Form Score</span>
                    <span className="text-secondary font-mono font-bold">{analysis.home_team.form_score}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Goals Scored</span>
                    <span className="text-primary font-mono font-bold">{analysis.home_team.stats.goals_scored}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Goals Conceded</span>
                    <span className="text-risk-high font-mono font-bold">{analysis.home_team.stats.goals_conceded}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Home Record</span>
                    <span className="text-white font-mono">
                      {analysis.home_team.stats.home_wins}W-{analysis.home_team.stats.home_draws}D-{analysis.home_team.stats.home_losses}L
                    </span>
                  </div>
                </div>
              </div>

              {/* Away Team Stats */}
              <div className="bg-white/5 border border-white/10 rounded-sm p-4">
                <div className="font-heading font-bold text-white mb-3">{analysis.away_team.name}</div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/50">Form Score</span>
                    <span className="text-secondary font-mono font-bold">{analysis.away_team.form_score}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Goals Scored</span>
                    <span className="text-primary font-mono font-bold">{analysis.away_team.stats.goals_scored}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Goals Conceded</span>
                    <span className="text-risk-high font-mono font-bold">{analysis.away_team.stats.goals_conceded}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Away Record</span>
                    <span className="text-white font-mono">
                      {analysis.away_team.stats.away_wins}W-{analysis.away_team.stats.away_draws}D-{analysis.away_team.stats.away_losses}L
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </details>
        </div>
      )}
    </div>
  );
};
